import { useEffect, useCallback, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { track } from '@/lib/analytics';
import '@/styles/about-overlay.css';

const ease = [0.22, 1, 0.36, 1];

const panels = [
  {
    title: 'WHAT CAME BEFORE',
    bg: '/images/story-p1.jpg',
    body: `Too many platforms are loud, overcrowded, ad-heavy — or just empty.

Built for volume. Measured in swipes.

Culturally misaligned with the people who most need them to work.`,
  },
  {
    title: "WHO IT'S FOR",
    bg: null,
    video: '/images/story-p2.mp4',
    body: `Trans women and femmes have been left trying to connect in spaces never truly built for their dynamic.

So have the people drawn to them.

NYMPHÉ was created to give both sides a space that makes more sense from the beginning.`,
  },
  {
    title: 'WHAT CHANGED',
    bg: '/images/story-p3.png',
    body: `NYMPHÉ was made deliberately.

A private space that centres trans women and femmes, and welcomes admirers with clarity.

A more intentional alternative — without the chaos, the mismatch, or the dead-end feel of traditional apps.`,
  },
  {
    title: 'HOW IT FEELS',
    bg: null,
    body: `Slower. Quieter. More intentional.

Less noise. Less explaining. Less friction.

Just real connection — between people who actually understand what this is.`,
  },
];

export const AboutOverlay = ({ isOpen, onClose }) => {
  const [current, setCurrent] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const touchStartX = useRef(0);
  const touchDelta = useRef(0);
  const closingTimer = useRef(null);
  const preloaded = useRef(false);

  // Preload panel images on first open
  useEffect(() => {
    if (isOpen && !preloaded.current) {
      preloaded.current = true;
      panels.forEach(p => {
        if (p.bg) { const img = new Image(); img.src = p.bg; }
        if (p.video) { const v = document.createElement('video'); v.src = p.video; v.preload = 'auto'; }
      });
    }
  }, [isOpen]);

  // Reset on open
  useEffect(() => {
    if (isOpen) { setCurrent(0); setIsClosing(false); }
  }, [isOpen]);

  // Cleanup pending timer on unmount
  useEffect(() => {
    return () => { if (closingTimer.current) clearTimeout(closingTimer.current); };
  }, []);

  const startClosing = useCallback((reason = 'unknown') => {
    if (isClosing) return;
    track('story_overlay_dismissed', { reason });
    setIsClosing(true);
    closingTimer.current = setTimeout(() => {
      setCurrent(0);
      setIsClosing(false);
      onClose();
    }, 1200);
  }, [isClosing, onClose]);

  const goNext = useCallback(() => {
    if (isClosing) return;
    if (current < panels.length - 1) setCurrent(current + 1);
    else startClosing('completed');
  }, [current, isClosing, startClosing]);

  const goPrev = useCallback(() => {
    if (isClosing) return;
    if (current > 0) setCurrent(current - 1);
  }, [current, isClosing]);

  // Keyboard
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); startClosing('esc'); }
      else if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, goNext, goPrev, startClosing]);

  // Lock the snap scroll container (not document.body — the homepage scrolls inside .nv3-page.nv3-snap)
  useEffect(() => {
    const container = document.querySelector('.nv3-page.nv3-snap');
    if (!container) return;
    if (isOpen) container.style.overflow = 'hidden';
    else container.style.overflow = '';
    return () => { container.style.overflow = ''; };
  }, [isOpen]);

  // Touch / swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; touchDelta.current = 0; };
  const onTouchMove = (e) => { touchDelta.current = e.touches[0].clientX - touchStartX.current; };
  const onTouchEnd = () => {
    if (touchDelta.current < -50) goNext();
    else if (touchDelta.current > 50) goPrev();
    touchDelta.current = 0;
  };

  const handleCardClick = (e) => {
    // Ignore clicks that originated on the arrows
    if (e.target.closest('.story-arrow')) return;
    goNext();
  };

  const panel = panels[current];
  const showLeft = current > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="story-root"
          data-testid="about-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: isClosing ? 0 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: isClosing ? 0.65 : 0.4, delay: isClosing ? 0.45 : 0, ease }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <motion.div
            className="story-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : 1 }}
            transition={{ duration: isClosing ? 0.65 : 0.4, delay: isClosing ? 0.45 : 0, ease }}
          />

          <motion.div
            className="story-card"
            onClick={handleCardClick}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{
              opacity: isClosing ? 0 : 1,
              scale: isClosing ? 0.98 : 1,
            }}
            transition={{
              duration: isClosing ? 0.6 : 0.5,
              delay: isClosing ? 0.4 : 0.1,
              ease,
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={`panel-${current}`}
                className="story-panel"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease }}
                data-testid={`story-panel-${current}`}
              >
                {panel.video ? (
                  <>
                    <video
                      src={panel.video}
                      className="story-img"
                      autoPlay
                      muted
                      playsInline
                      preload="auto"
                      data-testid="story-video"
                      onEnded={(e) => {
                        try {
                          e.currentTarget.pause();
                          // Hold on last frame — do not reset
                          e.currentTarget.currentTime = Math.max(0, e.currentTarget.duration - 0.03);
                        } catch { /* noop */ }
                      }}
                    />
                    <div className="story-img-gradient" />
                  </>
                ) : panel.bg ? (
                  <>
                    <img src={panel.bg} alt="" className="story-img" />
                    <div className="story-img-gradient" />
                  </>
                ) : (
                  <>
                    <div className="story-panel-dark" />
                    <div className="story-panel-rule" aria-hidden="true" />
                  </>
                )}
                <div className="story-text">
                  <motion.p
                    className="story-title"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{
                      opacity: isClosing && current === panels.length - 1 ? 0 : 1,
                      y: 0,
                    }}
                    transition={{
                      duration: isClosing ? 0.4 : 0.4,
                      delay: isClosing ? 0 : 0.2,
                      ease,
                    }}
                  >
                    {panel.title}
                  </motion.p>
                  <motion.p
                    className="story-body"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{
                      opacity: isClosing && current === panels.length - 1 ? 0 : 1,
                      y: 0,
                    }}
                    transition={{
                      duration: isClosing ? 0.4 : 0.5,
                      delay: isClosing ? 0 : 0.3,
                      ease,
                    }}
                  >
                    {panel.body}
                  </motion.p>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <motion.button
            type="button"
            className="story-close"
            onClick={(e) => { e.stopPropagation(); startClosing('close_btn'); }}
            aria-label="Close"
            data-testid="story-close"
            initial={{ opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : 1 }}
            transition={{ duration: 0.3, delay: isClosing ? 0 : 0.5, ease }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </motion.button>

          <motion.button
            type="button"
            className="story-arrow story-arrow--prev"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            aria-label="Previous"
            data-testid="story-prev"
            initial={{ opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : (showLeft ? 1 : 0) }}
            style={{ pointerEvents: showLeft && !isClosing ? 'auto' : 'none' }}
            transition={{ duration: 0.3, delay: isClosing ? 0.4 : 0.5, ease }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </motion.button>

          <motion.button
            type="button"
            className="story-arrow story-arrow--next"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            aria-label="Next"
            data-testid="story-next"
            initial={{ opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : 1 }}
            transition={{ duration: 0.3, delay: isClosing ? 0.4 : 0.5, ease }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </motion.button>

          <motion.div
            className="story-dots"
            data-testid="story-dots"
            initial={{ opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : 1 }}
            transition={{ duration: 0.3, delay: isClosing ? 0.4 : 0.5, ease }}
          >
            {panels.map((_, i) => (
              <span
                key={i}
                className={`story-dot ${i === current ? 'story-dot--active' : ''} ${i < current ? 'story-dot--done' : ''}`}
              />
            ))}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AboutOverlay;
